import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import ImageLibraryPicker from "@/components/common/ImageLibraryPicker";
import SectionHeader from "@/components/agent/shared/SectionHeader";
import VoiceAgentBasicForm from "@/components/voice-agents/VoiceAgentBasicForm";

import VoiceAgentVoiceForm from "@/components/voice-agents/VoiceAgentVoiceForm";
import VoiceAgentCallSettingsForm from "@/components/voice-agents/VoiceAgentCallSettingsForm";
import VoiceAgentBehaviorsSection from "@/components/voice-agents/VoiceAgentBehaviorsSection";
import AgentPostChatTab from "@/components/agent/tabs/AgentPostChatTab";
import AgentFormFieldsTab from "@/components/agent/tabs/AgentFormFieldsTab";
import VoiceAgentAdminTab from "@/components/voice-agents/VoiceAgentAdminTab";
import AgentKnowledgeTab from "@/components/agent/tabs/AgentKnowledgeTab";
import { TwilioPhoneNumberConnect } from "@/components/TwilioPhoneNumberConnect";
import PhoneNumberStatusCard from "@/components/voice-agents/PhoneNumberStatusCard";
import { voiceAgentService, VoiceAgent } from "@/services/voiceAgentService";
import VoiceAgentContextMenu from "@/components/voice/VoiceAgentContextMenu";
import {
  Settings,
  Wrench,
  Target,
  MessageSquare,
  Volume2,
  FormInput,
  ArrowLeft,
  AlignJustify,
  X,
  Edit,
  Check,
  Upload,
  Image,
  RefreshCw,
  Bell,
  Shield,
  BookOpen,
} from "lucide-react";

export default function VoiceAgentDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, organization, isAdmin } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Get current tabs from URL params with defaults
  const currentTab = searchParams.get("tab") || "configuration";
  const currentFormFieldTab = searchParams.get("formFieldTab") || "in-call";

  const [agent, setAgent] = useState<VoiceAgent | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>(currentTab);
  const [activeFormFieldTab, setActiveFormFieldTab] =
    useState<string>(currentFormFieldTab);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingAgent, setIsUpdatingAgent] = useState(false);

  // Agent editing states
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState("");
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  useEffect(() => {
    if (id) {
      fetchAgent();
    }
  }, [id]);

  useEffect(() => {
    if (agent) {
      setTitle(agent.title || "");
      setName(agent.agent_name || "");
    }
  }, [agent]);

  // Sync state with URL changes
  useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  useEffect(() => {
    setActiveFormFieldTab(currentFormFieldTab);
  }, [currentFormFieldTab]);

  const fetchAgent = async () => {
    if (!id) return;

    try {
      setLoading(true);
      console.log("🔍 Fetching voice agent with ID:", id);
      const { data, error } = await voiceAgentService.getVoiceAgent(id);
      if (error) throw error;

      console.log("✅ Fetched voice agent data:", {
        data: data,
        hasId: !!data?.id,
        id: data?.id,
        title: data?.title,
      });

      setAgent(data);
    } catch (error) {
      console.error("Error fetching voice agent:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load voice agent details",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateAgent = async (data: Partial<VoiceAgent>): Promise<boolean> => {
    console.log("🔄 updateAgent called with:", {
      agentId: agent?.id,
      hasAgent: !!agent,
      updateData: data,
    });

    if (!agent?.id) {
      console.error("❌ No agent ID available for update");
      return false;
    }

    try {
      const { error } = await voiceAgentService.updateVoiceAgent(
        agent.id,
        data,
      );
      if (error) throw error;

      console.log("✅ Agent updated successfully, updating local state");
      setAgent((prev) => {
        const newAgent = prev ? { ...prev, ...data } : null;
        console.log("🔄 New agent state after update:", {
          hasId: !!newAgent?.id,
          id: newAgent?.id,
          title: newAgent?.title,
        });
        return newAgent;
      });
      return true;
    } catch (error) {
      console.error("Error updating voice agent:", error);
      return false;
    }
  };

  // Helper function to update URL params
  const updateUrlParams = (updates: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      newParams.set(key, value);
    });
    setSearchParams(newParams);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    updateUrlParams({ tab });
  };

  const handleFormFieldTabChange = (tab: string) => {
    setActiveFormFieldTab(tab);
    updateUrlParams({ formFieldTab: tab });
  };

  const handleStatusToggle = async () => {
    if (!agent) return;

    setIsUpdatingStatus(true);
    try {
      const newStatus = !agent.is_active;
      const success = await updateAgent({ is_active: newStatus });

      if (success) {
        toast({
          title: "Status updated",
          description: `Voice agent ${newStatus ? "enabled" : "disabled"} successfully`,
        });
      } else {
        throw new Error("Failed to update status");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update voice agent status",
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleTitleEdit = () => setIsEditingTitle(true);
  const handleTitleSave = async () => {
    if (title !== agent?.title) {
      const success = await updateAgent({ title });
      if (success) {
        toast({
          title: "Title updated",
          description: "Voice agent title has been updated",
        });
      }
    }
    setIsEditingTitle(false);
  };

  const handleNameEdit = () => setIsEditingName(true);
  const handleNameSave = async () => {
    if (name !== agent?.agent_name) {
      const success = await updateAgent({ agent_name: name });
      if (success) {
        toast({
          title: "Name updated",
          description: "Voice agent name has been updated",
        });
      }
    }
    setIsEditingName(false);
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !agent) return;

    setIsUploadingAvatar(true);
    try {
      // TODO: Implement actual file upload logic
      // For now, just remove the misleading success toast
      console.log("Avatar upload not yet implemented");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: "Failed to upload avatar",
      });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleSelectFromLibrary = async (url: string) => {
    const success = await updateAgent({ avatar_url: url });
    if (success) {
      toast({
        title: "Avatar updated",
        description: "Avatar has been updated from library",
      });
    }
  };

  const handlePublishOrUpdateAgent = async () => {
    console.log("🚀 handlePublishOrUpdateAgent called with agent:", {
      agent: agent,
      agentId: agent?.id,
      hasAgent: !!agent,
      agentKeys: agent ? Object.keys(agent) : "no agent",
    });

    if (!agent) {
      console.error("❌ No agent available");
      return;
    }

    if (!agent.id) {
      console.error("❌ Agent ID is missing:", agent);
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "Agent ID is missing. Please refresh the page and try again.",
      });
      return;
    }

    setIsUpdatingAgent(true);
    try {
      let data, error;

      if (!agent.vapi_assistant_id) {
        // First time - publish to VAPI
        console.log("🚀 Publishing voice agent to VAPI for agent:", agent.id);
        ({ data, error } = await voiceAgentService.publishVoiceAgentToVapi(
          agent.id,
        ));
      } else {
        // Already published - update in VAPI
        console.log("🔄 Updating voice agent in VAPI for agent:", agent.id);
        ({ data, error } = await voiceAgentService.updateVoiceAgentInVapi(
          agent.id,
          {
            title: agent.title,
            agent_name: agent.agent_name,
            description: agent.description || undefined,
            instructions: agent.instructions,
            voice_provider: agent.voice_provider,
            voice_id: agent.voice_id,
            ai_temperature: agent.ai_temperature || undefined,
            first_message: agent.first_message,
            end_call_message: agent.end_call_message || undefined,
            max_duration_seconds: agent.max_duration_seconds || undefined,
            background_sound_enabled:
              agent.background_sound_enabled || undefined,
            avatar_url: agent.avatar_url || undefined,
          },
        ));
      }

      if (error) throw error;

      if (data) {
        setAgent(data);
      }

      toast({
        title: agent.vapi_assistant_id ? "Agent updated" : "Agent published",
        description: agent.vapi_assistant_id
          ? "Voice agent has been successfully updated"
          : "Voice agent has been successfully published",
      });
    } catch (error) {
      console.error("Error with agent operation:", error);
      toast({
        variant: "destructive",
        title: agent.vapi_assistant_id ? "Update failed" : "Publish failed",
        description: agent.vapi_assistant_id
          ? "Failed to update voice agent. Please try again."
          : "Failed to publish voice agent. Please try again.",
      });
    } finally {
      setIsUpdatingAgent(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading voice agent...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!agent) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-gray-600">Voice agent not found</p>
            <Button onClick={() => navigate("/voice-agents")} className="mt-4">
              Back to Voice Agents
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const brandColor = organization?.brand_color || "#8aa3d9";

  return (
    <MainLayout>
      <div className="w-full flex flex-1 bg-white min-h-0">
        {/* Left Sidebar - Hidden on mobile */}
        <div
          className={`w-64 bg-[#fafbfc] border-r border-gray-200 flex flex-col ${isMobile ? "hidden" : ""}`}
        >
          <div className="p-4 space-y-6 pt-12">
            {/* Avatar */}
            <div className="flex justify-center">
              <div className="relative group">
                <Avatar className="h-20 w-20">
                  {agent.avatar_url ? (
                    <AvatarImage src={agent.avatar_url} alt={agent.title} />
                  ) : (
                    <AvatarFallback
                      className="text-xl font-semibold"
                      style={{
                        backgroundColor: brandColor,
                        color: ["#fff", "#ffffff"].includes(brandColor)
                          ? "#000"
                          : "#fff",
                      }}
                    >
                      {getInitials(agent.agent_name || agent.title || "A")}
                    </AvatarFallback>
                  )}
                </Avatar>

                {/* Avatar Upload Overlay */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-full transition-all duration-200 flex items-center justify-center">
                      <Edit className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0">
                    <div className="space-y-2 p-4">
                      <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded-md">
                        <Upload className="h-4 w-4" />
                        <span className="text-sm">Upload new image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          disabled={isUploadingAvatar}
                          className="hidden"
                        />
                      </label>
                      <ImageLibraryPicker
                        onSelectImage={handleSelectFromLibrary}
                        triggerButton={
                          <div className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded-md">
                            <Image className="h-4 w-4" />
                            <span className="text-sm">Choose from library</span>
                          </div>
                        }
                        imageType="avatar"
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Agent Name & Title */}
            <div className="text-center space-y-1">
              {/* Agent Name - Primary */}
              {isEditingName ? (
                <div className="flex items-center space-x-2">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="text-center text-[22px] font-semibold h-9"
                    onBlur={handleNameSave}
                    onKeyDown={(e) => e.key === "Enter" && handleNameSave()}
                    autoFocus
                  />
                  <Button size="sm" variant="ghost" onClick={handleNameSave}>
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="group cursor-pointer" onClick={handleNameEdit}>
                  <h1 className="text-[22px] font-semibold text-gray-900 group-hover:text-gray-700 flex items-center justify-center gap-1">
                    {agent.agent_name || "Voice Agent"}
                    <Edit className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h1>
                </div>
              )}

              {/* Title - Secondary */}
              {isEditingTitle ? (
                <div className="flex items-center space-x-2">
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="text-center text-base h-8 text-gray-500"
                    onBlur={handleTitleSave}
                    onKeyDown={(e) => e.key === "Enter" && handleTitleSave()}
                    autoFocus
                  />
                  <Button size="sm" variant="ghost" onClick={handleTitleSave}>
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="group cursor-pointer" onClick={handleTitleEdit}>
                  <p className="text-base text-gray-500 group-hover:text-gray-400 flex items-center justify-center gap-1">
                    {agent.title || "Untitled Voice Agent"}
                    <Edit className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </p>
                </div>
              )}
            </div>

            {/* Navigation Tabs - Vertical Layout */}
            <nav className="space-y-1">
              <button
                onClick={() => handleTabChange("configuration")}
                className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                  activeTab === "configuration"
                    ? "bg-[#8aa3d9] text-white"
                    : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                }`}
              >
                <Settings className="h-4 w-4" />
                Instructions
              </button>
              <button
                onClick={() => handleTabChange("call-settings")}
                className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                  activeTab === "call-settings"
                    ? "bg-[#8aa3d9] text-white"
                    : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                }`}
              >
                <Wrench className="h-4 w-4" />
                Call Settings
              </button>

              <button
                onClick={() => handleTabChange("voice")}
                className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                  activeTab === "voice"
                    ? "bg-[#8aa3d9] text-white"
                    : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                }`}
              >
                <Volume2 className="h-4 w-4" />
                Voice
              </button>

              <button
                onClick={() => handleTabChange("forms")}
                className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                  activeTab === "forms"
                    ? "bg-[#8aa3d9] text-white"
                    : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                }`}
              >
                <FormInput className="h-4 w-4" />
                Forms
              </button>
              <button
                onClick={() => handleTabChange("knowledge")}
                className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                  activeTab === "knowledge"
                    ? "bg-[#8aa3d9] text-white"
                    : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                }`}
              >
                <BookOpen className="h-4 w-4" />
                Knowledge
              </button>
              <button
                onClick={() => handleTabChange("notifications")}
                className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                  activeTab === "notifications"
                    ? "bg-[#8aa3d9] text-white"
                    : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                }`}
              >
                <Bell className="h-4 w-4" />
                Notifications
              </button>

              {/* Admin Tab - Only show for super admins */}
              {isAdmin && (
                <button
                  onClick={() => handleTabChange("admin")}
                  className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                    activeTab === "admin"
                      ? "bg-[#8aa3d9] text-white"
                      : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                  }`}
                >
                  <Shield className="h-4 w-4" />
                  Admin
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col h-full min-h-0">
          {/* Top Navigation - Mobile responsive */}
          <div className="border-b border-gray-200 bg-white flex-shrink-0">
            <div className="flex items-center justify-between px-4 py-3">
              {/* Mobile controls - Back button and Burger menu */}
              {isMobile && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(-1)}
                    className="h-10 w-10 p-0"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMobileSidebarOpen(true)}
                    className="h-10 w-10 p-0"
                  >
                    <AlignJustify className="h-5 w-5" />
                  </Button>
                </div>
              )}

              {/* Title */}
              <div className="flex-1">
                <h1 className="text-lg font-semibold text-gray-900">
                  {agent.agent_name || "Voice Agent"}
                </h1>
              </div>

              {/* Phone Number Status Card and Controls */}
              <div className="flex items-center gap-3">
                {/* Phone Number Status Card */}
                <div className="w-64">
                  <PhoneNumberStatusCard
                    agent={agent}
                    onAgentUpdate={setAgent}
                  />
                </div>

                <Button
                  onClick={handlePublishOrUpdateAgent}
                  disabled={isUpdatingAgent}
                  className={
                    agent.vapi_assistant_id
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-green-600 hover:bg-green-700 text-white"
                  }
                >
                  {isUpdatingAgent ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {agent.vapi_assistant_id
                        ? "Updating..."
                        : "Publishing..."}
                    </>
                  ) : (
                    <>
                      {agent.vapi_assistant_id ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Update Agent
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Publish Agent
                        </>
                      )}
                    </>
                  )}
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Enable</span>
                  <Switch
                    checked={agent.is_active || false}
                    onCheckedChange={handleStatusToggle}
                    disabled={isUpdatingStatus}
                  />
                  <Badge variant={agent.is_active ? "default" : "secondary"}>
                    {agent.is_active ? "Active" : "Inactive"}
                  </Badge>

                  {/* Context Menu with Archive */}
                  <div className="ml-2">
                    <VoiceAgentContextMenu
                      agent={agent}
                      onArchiveSuccess={() => navigate("/voice-agents")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area - Scrollable */}
          <div className="flex-1 overflow-auto min-h-0">
            <div className="max-w-[56rem] mx-auto px-4 sm:px-6 py-4 sm:py-8">
              {/* Instructions Tab */}
              {activeTab === "configuration" && (
                <div className="space-y-8">
                  {/* Instructions */}
                  <Card className="border rounded-2xl">
                    <CardContent className="p-5">
                      <SectionHeader
                        title="Instructions"
                        description="Define how your voice agent should behave and respond"
                        icon={<Target className="h-5 w-5" />}
                      />
                      <div className="mt-4">
                        <VoiceAgentBasicForm
                          agent={agent}
                          updateAgent={updateAgent}
                          showOnly={["instructions"]}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Behaviors */}
                  <VoiceAgentBehaviorsSection
                    agent={agent}
                    updateAgent={updateAgent}
                  />
                </div>
              )}

              {/* Call Settings Tab */}
              {activeTab === "call-settings" && (
                <div className="space-y-8">
                  {/* Call Settings */}
                  <VoiceAgentCallSettingsForm
                    agent={agent}
                    updateAgent={updateAgent}
                  />
                </div>
              )}

              {/* Voice Tab */}
              {activeTab === "voice" && (
                <div className="space-y-8">
                  {/* Voice Settings */}
                  <VoiceAgentVoiceForm
                    agent={agent}
                    updateAgent={updateAgent}
                  />
                </div>
              )}

              {/* Forms Tab */}
              {activeTab === "forms" && (
                <div className="space-y-8">
                  {/* Forms Tab Navigation */}
                  <div className="border-b border-gray-200">
                    <nav className="flex space-x-8">
                      <button
                        onClick={() => handleFormFieldTabChange("in-call")}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeFormFieldTab === "in-call"
                            ? "border-[#8aa3d9] text-[#8aa3d9]"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        In-Call Fields
                      </button>
                      <button
                        onClick={() => handleFormFieldTabChange("post-call")}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeFormFieldTab === "post-call"
                            ? "border-[#8aa3d9] text-[#8aa3d9]"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        Post-Call Fields
                      </button>
                    </nav>
                  </div>

                  {/* Form Fields Content */}
                  {activeFormFieldTab === "in-call" && (
                    <AgentFormFieldsTab
                      agent={{
                        id: agent.id,
                        title: agent.title,
                        agent_name: agent.agent_name,
                        contact_info: null, // Voice agents don't use contact_info
                        organization_id: agent.organization_id,
                        created_by: agent.created_by,
                        created_at: agent.created_at,
                        updated_at: agent.updated_at,
                        // Add other required agent fields with safe defaults
                        is_active: agent.is_active,
                        is_public: false,
                        is_template: false,
                        instructions: agent.instructions,
                        description: agent.description,
                        avatar_url: agent.avatar_url,
                        welcome_message: null,
                        thank_you_message: null,
                        conversation_starters: null,
                        restrictions: null,
                        goal: null,
                        ai_temperature: agent.ai_temperature,
                        ai_creation_prompt: null,
                        notification_email: null,
                        notification_webhook_url: null,
                        persona_id: null,
                        industry_id: null,
                        public_id: null,
                        template_source_id: null,
                        hidden_template_instructions: null,
                        banner: null,
                        category: null,
                        splash_page_title: null,
                        splash_page_subtitle: null,
                        start_button_text: null,
                        allow_file_search: null,
                        allow_web_search: null,
                        skills_scheduling: null,
                        fts: null,
                      }}
                      fieldSection="in_conversation"
                      isVoiceAgent={true}
                    />
                  )}
                  {activeFormFieldTab === "post-call" && (
                    <AgentFormFieldsTab
                      agent={{
                        id: agent.id,
                        title: agent.title,
                        agent_name: agent.agent_name,
                        contact_info: null, // Voice agents don't use contact_info
                        organization_id: agent.organization_id,
                        created_by: agent.created_by,
                        created_at: agent.created_at,
                        updated_at: agent.updated_at,
                        // Add other required agent fields with safe defaults
                        is_active: agent.is_active,
                        is_public: false,
                        is_template: false,
                        instructions: agent.instructions,
                        description: agent.description,
                        avatar_url: agent.avatar_url,
                        welcome_message: null,
                        thank_you_message: null,
                        conversation_starters: null,
                        restrictions: null,
                        goal: null,
                        ai_temperature: agent.ai_temperature,
                        ai_creation_prompt: null,
                        notification_email: null,
                        notification_webhook_url: null,
                        persona_id: null,
                        industry_id: null,
                        public_id: null,
                        template_source_id: null,
                        hidden_template_instructions: null,
                        banner: null,
                        category: null,
                        splash_page_title: null,
                        splash_page_subtitle: null,
                        start_button_text: null,
                        allow_file_search: null,
                        allow_web_search: null,
                        skills_scheduling: null,
                        fts: null,
                      }}
                      fieldSection="post_conversation"
                      isVoiceAgent={true}
                    />
                  )}
                </div>
              )}

              {/* Knowledge Tab */}
              {activeTab === "knowledge" && (
                <div className="space-y-8">
                  <AgentKnowledgeTab agentId={agent.id} agentType="voice" />
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === "notifications" && (
                <div className="space-y-8">
                  <AgentPostChatTab agent={agent} updateAgent={updateAgent} />
                </div>
              )}

              {/* Admin Tab - Only show for super admins */}
              {activeTab === "admin" && isAdmin && (
                <div className="space-y-8">
                  <VoiceAgentAdminTab agent={agent} updateAgent={updateAgent} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobile && (
          <div
            className={`fixed inset-0 z-50 transition-all duration-300 ${
              isMobileSidebarOpen
                ? "opacity-100 visible"
                : "opacity-0 invisible"
            }`}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setIsMobileSidebarOpen(false)}
            />

            {/* Sidebar Content */}
            <div
              className={`absolute left-0 top-0 h-full w-64 bg-[#fafbfc] transform transition-transform duration-300 ${
                isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
              }`}
            >
              <div className="p-4 space-y-6 pt-12 h-full overflow-y-auto">
                {/* Close button */}
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsMobileSidebarOpen(false)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Avatar */}
                <div className="flex justify-center">
                  <Avatar className="h-20 w-20">
                    {agent.avatar_url ? (
                      <AvatarImage src={agent.avatar_url} alt={agent.title} />
                    ) : (
                      <AvatarFallback
                        className="text-xl font-semibold"
                        style={{
                          backgroundColor: brandColor,
                          color: ["#fff", "#ffffff"].includes(brandColor)
                            ? "#000"
                            : "#fff",
                        }}
                      >
                        {getInitials(agent.agent_name || agent.title || "A")}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </div>

                {/* Agent Name & Title */}
                <div className="text-center space-y-1">
                  <h1 className="text-[22px] font-semibold text-gray-900">
                    {agent.agent_name || "Voice Agent"}
                  </h1>
                  <p className="text-base text-gray-500">
                    {agent.title || "Untitled Voice Agent"}
                  </p>
                </div>

                {/* Navigation Tabs */}
                <nav className="space-y-1">
                  <button
                    onClick={() => {
                      handleTabChange("configuration");
                      setIsMobileSidebarOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                      activeTab === "configuration"
                        ? "bg-[#8aa3d9] text-white"
                        : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                    }`}
                  >
                    <Settings className="h-4 w-4" />
                    Instructions
                  </button>
                  <button
                    onClick={() => {
                      handleTabChange("call-settings");
                      setIsMobileSidebarOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                      activeTab === "call-settings"
                        ? "bg-[#8aa3d9] text-white"
                        : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                    }`}
                  >
                    <Wrench className="h-4 w-4" />
                    Call Settings
                  </button>

                  <button
                    onClick={() => {
                      handleTabChange("voice");
                      setIsMobileSidebarOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                      activeTab === "voice"
                        ? "bg-[#8aa3d9] text-white"
                        : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                    }`}
                  >
                    <Volume2 className="h-4 w-4" />
                    Voice
                  </button>

                  <button
                    onClick={() => {
                      handleTabChange("forms");
                      setIsMobileSidebarOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                      activeTab === "forms"
                        ? "bg-[#8aa3d9] text-white"
                        : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                    }`}
                  >
                    <FormInput className="h-4 w-4" />
                    Forms
                  </button>
                  <button
                    onClick={() => {
                      handleTabChange("knowledge");
                      setIsMobileSidebarOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                      activeTab === "knowledge"
                        ? "bg-[#8aa3d9] text-white"
                        : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                    }`}
                  >
                    <BookOpen className="h-4 w-4" />
                    Knowledge
                  </button>
                  <button
                    onClick={() => {
                      handleTabChange("notifications");
                      setIsMobileSidebarOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                      activeTab === "notifications"
                        ? "bg-[#8aa3d9] text-white"
                        : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                    }`}
                  >
                    <Bell className="h-4 w-4" />
                    Notifications
                  </button>

                  {/* Admin Tab - Only show for super admins */}
                  {isAdmin && (
                    <button
                      onClick={() => {
                        handleTabChange("admin");
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-3 ${
                        activeTab === "admin"
                          ? "bg-[#8aa3d9] text-white"
                          : "text-gray-700 hover:text-[#8aa3d9] hover:bg-[#8aa3d9] hover:bg-opacity-5"
                      }`}
                    >
                      <Shield className="h-4 w-4" />
                      Admin
                    </button>
                  )}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
